'use client';

import { useState } from 'react';

interface PreviewBadgeProps {
  status: string;
  themeId: string;
  source: string;
}

/**
 * Floating preview mode badge that shows in bottom-right corner.
 * Click to expand and see full details, click again to collapse.
 */
export function PreviewBadge({ status, themeId, source }: PreviewBadgeProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="fixed bottom-4 right-4 z-[60]">
      {isExpanded ? (
        // Expanded panel
        <div 
          className="bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 text-white rounded-lg shadow-xl p-4 min-w-[240px] animate-in slide-in-from-bottom-2 fade-in duration-200"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
              </span>
              <span className="text-sm font-semibold">Preview Mode</span>
            </div>
            <button
              onClick={() => setIsExpanded(false)}
              className="text-white/80 hover:text-white transition-colors p-1 -m-1"
              aria-label="Collapse preview details"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="space-y-1.5 text-xs">
            <div className="flex justify-between">
              <span className="text-white/70">Status:</span>
              <span className="font-medium">{status}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/70">Theme:</span>
              <span className="font-medium">{themeId}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/70">Source:</span>
              <span className="font-medium">{source}</span>
            </div>
          </div>
        </div>
      ) : (
        // Collapsed badge
        <button
          onClick={() => setIsExpanded(true)}
          className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full shadow-lg px-3 py-1.5 text-xs font-medium flex items-center gap-2 hover:shadow-xl hover:scale-105 transition-all duration-200"
          aria-label="View preview mode details"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
          </span>
          Preview
        </button>
      )}
    </div>
  );
}
